import { useCallback, useRef, useState } from 'react';
import { listContacts } from 'services/lead/leadServices';
import { generateFullName } from 'utils/generateFullNameUtils';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Maps a list-contacts API row to an AutoComplete option with label + email.
 * @param {object} contact
 * @returns {{ label: string, email: string } | null}
 */
export function mapContactToSuggestion(contact) {
  const email = (contact?.email || '').trim();
  if (!email) return null;

  if (contact.contact_type === 'person' || contact.first_name) {
    const name = generateFullName(contact).trim();
    return {
      label: name ? `${name} (${email})` : email,
      email,
    };
  }

  const title = (contact.title || '').trim();
  return {
    label: title ? `${title} (${email})` : email,
    email,
  };
}

/**
 * Debounced search against GET /list-contacts for compose-mail "To" recipient picker.
 */
export function useMailboxRecipientContacts() {
  const [suggestions, setSuggestions] = useState([]);
  const timerRef = useRef(null);

  const searchContacts = useCallback((event) => {
    const query = (event?.query || '').trim();
    if (timerRef.current) clearTimeout(timerRef.current);

    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    timerRef.current = setTimeout(async () => {
      try {
        const response = await listContacts({
          contactType: '',
          contactStatus: 'ACTIVE',
          keyWord: query,
          pageLimit: 25,
          excludeFromLoading: true,
        });
        const contacts = response?.data?.contacts || [];
        const mapped = contacts.map(mapContactToSuggestion).filter(Boolean);
        setSuggestions(mapped);
      } catch {
        setSuggestions([]);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  return { suggestions, searchContacts };
}
